import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";

import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context";

import {
  createPartnerMasterAccess,
  getPartnerMasterExists,
} from "../../clients.api";
import type { TCreatePartnerMasterInput } from "../../clients.types";
import {
  partnerPortalMasterFormSchema,
  type TPartnerPortalMasterFormData,
} from "./partner-portal-card.schema";

export interface IUsePartnerPortalCard {
  form: ReturnType<
    typeof useForm<TPartnerPortalMasterFormData>
  >;
  onSubmit: (values: TPartnerPortalMasterFormData) => void;
  masterExists: boolean | undefined;
  isLoadingExists: boolean;
  isSubmitting: boolean;
}

export function usePartnerPortalCard(
  organizationId: string
): IUsePartnerPortalCard {
  const { t } = useI18n();
  const { toast } = useToast();

  const form = useForm<TPartnerPortalMasterFormData>({
    resolver: zodResolver(partnerPortalMasterFormSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      use_representative_data: true,
    },
  });

  const existsQuery = useQuery({
    queryKey: ["partner-master-exists", organizationId],
    queryFn: () => getPartnerMasterExists(organizationId),
    enabled: Boolean(organizationId),
  });

  const createMutation = useMutation({
    mutationFn: (data: TCreatePartnerMasterInput) =>
      createPartnerMasterAccess(organizationId, data),
    onSuccess: () => {
      toast({
        title: t("generic.success"),
        description: t("clients.partnerPortal.createSuccess"),
      });
      void existsQuery.refetch();
      form.reset({
        email: "",
        password: "",
        confirmPassword: "",
        use_representative_data: true,
        manual: undefined,
      });
    },
    onError: (err: Error) => {
      toast({
        title: t("generic.error"),
        description: err.message || t("clients.partnerPortal.createError"),
        variant: "destructive",
      });
    },
  });

  const onSubmit = React.useCallback(
    (values: TPartnerPortalMasterFormData) => {
      const payload: TCreatePartnerMasterInput = {
        email: values.email,
        password: values.password,
        use_representative_data: values.use_representative_data,
        manual: values.use_representative_data
          ? undefined
          : values.manual
            ? {
                first_name: values.manual.first_name,
                last_name: values.manual.last_name,
                document: values.manual.document.replace(/\D/g, ""),
                document_type: values.manual.document_type,
                phone: values.manual.phone.replace(/\D/g, ""),
                birth_date: values.manual.birth_date,
                address: {
                  street: values.manual.address.street,
                  number: values.manual.address.number,
                  complement: values.manual.address.complement,
                  neighborhood: values.manual.address.neighborhood,
                  city: values.manual.address.city,
                  state: values.manual.address.state,
                  country: values.manual.address.country ?? "Brasil",
                  postal_code: values.manual.address.postal_code.replace(
                    /\D/g,
                    ""
                  ),
                },
              }
            : undefined,
      };
      createMutation.mutate(payload);
    },
    [createMutation]
  );

  return {
    form,
    onSubmit,
    masterExists: existsQuery.data?.exists,
    isLoadingExists: existsQuery.isLoading,
    isSubmitting: createMutation.isPending,
  };
}
